import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import DashboardLayout  from '../../components/common/DashboardLayout';
import SectorDots       from '../../components/admin/SectorDots';
import { useAuth } from '../../context/AuthContext';
import AdminNav from '../../components/admin/AdminNav';
import PageVisits from '../../components/admin/PageVisits';
import {
  getAdminMembers,
  getEngagementStats,
  getSlipFlags,
  exportCSV,
  updateMemberRole,
  createMember,
  createTrialMember,
} from '../../services/admin.service';

const PHASE_BADGE = {
  observing: 'bg-gray-100 text-gray-600',
  path_set:  'bg-blue-100 text-blue-700',
  active:    'bg-green-100 text-green-700',
};

/**
 * Admin dashboard — overview of all members.
 *
 * Shows:
 *   — Engagement stats bar (total, active, checked-in, nudges sent)
 *   — Slip flag alerts (members needing human attention)
 *   — Full member table with sector dots + last check-in
 */
export default function Admin() {
  const { user } = useAuth();
  const [stats,   setStats]   = useState(null);
  const [flags,   setFlags]   = useState([]);
  const [members, setMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error,   setError]   = useState(null);
  const [search,  setSearch]  = useState('');
  const [showAddMember, setShowAddMember] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  // Lets the Overview stat cards deep-link straight to "who are these N
  // members" instead of leaving that question unanswerable from the UI.
  const [filter,  setFilter]  = useState(searchParams.get('filter') || 'all'); // all | flagged | active | paying | founder_price | regular_price
  const setFilterAndUrl = (v) => {
    setFilter(v);
    setSearchParams(v === 'all' ? {} : { filter: v });
  };

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [statsData, flagsData, membersData] = await Promise.all([
        getEngagementStats(),
        getSlipFlags(),
        getAdminMembers(),
      ]);
      setStats(statsData);
      setFlags(flagsData.flags || []);
      setMembers(membersData.members || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Could not load admin data.');
    } finally {
      setLoading(false);
    }
  }, []);

  const toggleRole = async (member) => {
    const nextRole = member.role === 'admin' ? 'member' : 'admin';
    if (!window.confirm(`${nextRole === 'admin' ? 'Grant' : 'Remove'} admin access for ${member.name || member.email}?`)) return;
    await updateMemberRole(member.id, nextRole);
    load();
  };

  useEffect(() => { load(); }, [load]);

  // Filter + search
  const visible = members
    .filter(m => {
      if (filter === 'flagged') return m.slip_flag;
      // Same rules as the Overview tiles (Jenn, 2026-10-07).
      if (filter === 'active')  return m.is_engaged_7d;
      if (filter === 'paying')  return m.is_paying;
      if (filter === 'founder_price') return m.is_paying && !m.is_regular_price;
      if (filter === 'regular_price') return m.is_paying && m.is_regular_price;
      return true;
    })
    .filter(m => {
      if (!search) return true;
      const q = search.toLowerCase();
      // "day 238" or a bare number matches their real content-sequence day —
      // Jenn, 2026-09-06: "why can't I see these with a simple inquiry?"
      const dayMatch = /(?:^|\bday\s*)(\d+)$/i.exec(search.trim());
      if (dayMatch && m.daily_sequence_day === Number(dayMatch[1])) return true;
      return (m.name || '').toLowerCase().includes(q)
          || (m.email || '').toLowerCase().includes(q);
    });

  return (
    <DashboardLayout>

      {/* Header */}
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <div>
          <h1 className="font-serif text-2xl font-bold text-reset-brown">Member Overview</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            {stats ? `${stats.total_members} members` : '—'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddMember(true)}
            className="px-4 py-2 text-sm bg-reset-terracotta text-white rounded-lg font-medium hover:bg-reset-brown transition-colors"
          >
            + Add Member
          </button>
          <button
            onClick={exportCSV}
            className="px-4 py-2 text-sm border border-gray-200 rounded-lg text-gray-600 hover:border-reset-terracotta hover:text-reset-terracotta transition-colors"
          >
            ↓ Export CSV
          </button>
        </div>
      </div>

      <PageVisits />

      {showAddMember && (
        <AddMemberModal
          onClose={() => setShowAddMember(false)}
          onCreated={() => { setShowAddMember(false); load(); }}
        />
      )}

      <AdminNav />

      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 text-red-700 rounded-xl p-4 text-sm">
          {error}
        </div>
      )}

      {/* ── Stats bar ──────────────────────────────────────────── */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
          <StatCard label="Total members"   value={stats.total_members}   />
          <StatCard label="Active"          value={stats.active_members}  />
          <StatCard label="Checked in (7d)" value={stats.checked_in_7d}  />
          <StatCard label="Nudges (7d)"     value={stats.nudges_sent_7d} />
          <StatCard
            label="Avg score today"
            value={stats.avg_score_today != null ? stats.avg_score_today.toFixed(1) : '—'}
            unit="/10"
          />
        </div>
      )}

      {/* ── Slip flags ─────────────────────────────────────────── */}
      {flags.length > 0 && (
        <div className="mb-6 bg-red-50 border border-red-200 rounded-2xl p-4">
          <p className="text-sm font-bold text-red-800 mb-3 flex items-center gap-2">
            🚨 {flags.length} member{flags.length > 1 ? 's' : ''} need{flags.length === 1 ? 's' : ''} attention
          </p>
          <div className="space-y-2">
            {flags.map(flag => (
              <div key={flag.memberId} className="flex items-center justify-between bg-white rounded-xl px-4 py-2.5 text-sm">
                <div>
                  <span className="font-medium text-reset-brown">{flag.name || flag.email}</span>
                  <span className="text-red-600 text-xs ml-2">{flag.reason}</span>
                </div>
                <Link
                  to={`/admin/members/${flag.memberId}`}
                  className="text-xs font-medium text-reset-terracotta hover:underline"
                >
                  View →
                </Link>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Filter + search ─────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="flex gap-1 bg-gray-100 rounded-lg p-1 text-xs font-medium flex-wrap">
          {[['all','All'],['flagged','Flagged 🚨'],['active','Active'],['paying','Paying'],['founder_price','Founder price'],['regular_price','Regular price']].map(([v,l]) => (
            <button
              key={v}
              onClick={() => setFilterAndUrl(v)}
              className={`px-3 py-1.5 rounded-md transition-colors ${
                filter === v ? 'bg-white text-reset-brown shadow-sm' : 'text-gray-500 hover:text-reset-brown'
              }`}
            >
              {l}
            </button>
          ))}
        </div>
        <input
          type="search"
          placeholder="Search by name, email, or content day (e.g. day 238)…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="ml-auto flex-1 max-w-xs border border-gray-200 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta"
        />
      </div>

      {/* ── Member table ────────────────────────────────────────── */}
      {loading ? (
        <div className="space-y-2 animate-pulse">
          {[...Array(8)].map((_, i) => (
            <div key={i} className="h-14 bg-gray-200 rounded-xl" />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-2xl shadow-sm overflow-hidden">
          {/* Table header */}
          <div className="hidden md:grid grid-cols-[2fr_1fr_auto_auto_auto_auto] gap-4 px-5 py-2.5 bg-gray-50 border-b border-gray-100 text-xs font-semibold text-gray-500 uppercase tracking-wide">
            <span>Member</span>
            <span>Phase</span>
            <span>Sectors</span>
            <span>Day #</span>
            <span>Last check-in</span>
            <span />
          </div>

          {visible.length === 0 && (
            <p className="text-center text-gray-400 py-10 text-sm">No members match this filter.</p>
          )}

          {visible.map((m, i) => (
            <div
              key={m.id}
              className={`grid grid-cols-1 md:grid-cols-[2fr_1fr_auto_auto_auto_auto] gap-2 md:gap-4
                          items-center px-5 py-3.5 border-b border-gray-50 last:border-0
                          ${m.slip_flag ? 'bg-red-50/50' : ''}
                          ${i % 2 === 0 && !m.slip_flag ? 'bg-white' : ''}`}
            >
              {/* Name + email */}
              <div className="flex items-center gap-2 min-w-0">
                {m.slip_flag && <span title="Needs attention" className="text-red-500 shrink-0">🚨</span>}
                {m.avatar_url ? (
                  <img src={m.avatar_url} alt="" className="w-8 h-8 rounded-full object-cover shrink-0" />
                ) : (
                  <span aria-hidden="true" className="w-8 h-8 rounded-full bg-reset-sage text-white flex items-center justify-center text-xs font-bold shrink-0">
                    {(m.name || '?').trim().charAt(0).toUpperCase()}
                  </span>
                )}
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-reset-brown truncate flex items-center gap-1.5">
                    {m.name || '(no name)'}
                    {m.is_paying && (
                      <span
                        title={m.is_regular_price ? 'Paying — regular price' : 'Paying — founder price'}
                        className={`text-[10px] font-medium px-1.5 py-0.5 rounded-full shrink-0 ${
                          m.is_regular_price ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'
                        }`}
                      >
                        $ paying
                      </span>
                    )}
                  </p>
                  <p className="text-xs text-gray-400 truncate">{m.email}</p>
                </div>
              </div>

              {/* Phase badge */}
              <span className={`inline-flex items-center text-[11px] font-medium px-2 py-0.5 rounded-full w-fit ${PHASE_BADGE[m.onboarding_phase] || PHASE_BADGE.observing}`}>
                {m.onboarding_phase?.replace('_', ' ')}
              </span>

              {/* Sector dots */}
              <SectorDots sectors={m.sectors} />

              {/* Day-in-program (calendar days since joining) + real content-sequence
                  position (their actual daily_content day) — these can drift apart
                  (content dispatch paused, missing content, etc.), so shown separately
                  rather than conflated into one number. */}
              <div className="text-xs">
                <span className="text-gray-500">Day {m.day_in_program}</span>
                {m.daily_sequence_day != null && (
                  <span className="block text-[10px] text-gray-400 mt-0.5">Content day {m.daily_sequence_day}</span>
                )}
                {m.days_until_renewal <= 30 && (
                  <span className="block text-[10px] font-medium text-amber-600 mt-0.5">
                    Renews in {m.days_until_renewal}d
                  </span>
                )}
              </div>

              {/* Last check-in */}
              <span className={`text-xs ${
                !m.days_since_checkin ? 'text-gray-300' :
                m.days_since_checkin >= 5 ? 'text-red-500 font-semibold' :
                m.days_since_checkin >= 3 ? 'text-amber-600' : 'text-gray-500'
              }`}>
                {m.last_checkin_text || 'Never'}
              </span>

              {/* Detail link + admin toggle */}
              <div className="flex items-center gap-3 whitespace-nowrap">
                {m.role === 'admin' && (
                  <span className="text-[10px] font-medium px-2 py-0.5 rounded-full bg-reset-terracotta/10 text-reset-terracotta">
                    Admin
                  </span>
                )}
                {m.id !== user?.id && (
                  <button
                    onClick={() => toggleRole(m)}
                    className="text-xs font-medium text-gray-400 hover:text-reset-brown"
                  >
                    {m.role === 'admin' ? 'Remove admin' : 'Make admin'}
                  </button>
                )}
                <Link
                  to={`/admin/members/${m.id}`}
                  className="text-xs font-medium text-reset-terracotta hover:underline"
                >
                  View →
                </Link>
              </div>
            </div>
          ))}
        </div>
      )}
    </DashboardLayout>
  );
}

// ─── Add Member modal ─────────────────────────────────────────────────────────
// Manual escape hatch for the exceptions to the normal Stripe-webhook path —
// comp'd access, a manual entry, etc. Created with no password, same as a
// fresh Stripe-created account; the member sets their own via "sign in," or
// an admin uses "Set temp password" from their profile.
function AddMemberModal({ onClose, onCreated }) {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [isTrial, setIsTrial] = useState(false);
  const [trialDays, setTrialDays] = useState(7);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;
    setSaving(true);
    setError(null);
    try {
      if (isTrial) {
        await createTrialMember({ name: name.trim(), email: email.trim(), phone: phone.trim() || undefined, trialDays });
      } else {
        await createMember({ name: name.trim(), email: email.trim(), phone: phone.trim() || undefined });
      }
      onCreated();
    } catch (err) {
      setError(err.response?.data?.error || 'Could not create this member.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 px-4" onClick={onClose}>
      <form
        onSubmit={submit}
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl shadow-lg p-6 w-full max-w-sm space-y-3"
      >
        <h2 className="font-serif text-xl font-bold text-reset-brown">Add Member</h2>
        <p className="text-xs text-gray-500">
          For comp'd access or a manual entry — real signups still come through Stripe automatically.
        </p>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Full name</label>
          <input value={name} onChange={e => setName(e.target.value)} required autoFocus
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Email</label>
          <input type="email" value={email} onChange={e => setEmail(e.target.value)} required
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <div>
          <label className="block text-xs font-medium text-gray-500 mb-1">Phone (optional)</label>
          <input value={phone} onChange={e => setPhone(e.target.value)}
            className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
        </div>
        <label className="flex items-center gap-2 text-sm text-reset-brown pt-1">
          <input type="checkbox" checked={isTrial} onChange={e => setIsTrial(e.target.checked)}
            className="rounded border-gray-300 text-reset-terracotta focus:ring-reset-terracotta" />
          Real trial for a prospect (their own account, own onboarding — not a demo login)
        </label>
        {isTrial && (
          <div>
            <label className="block text-xs font-medium text-gray-500 mb-1">Trial length (days)</label>
            <input type="number" min="1" value={trialDays} onChange={e => setTrialDays(e.target.value)}
              className="w-24 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-reset-terracotta" />
          </div>
        )}
        {error && <p className="text-xs text-red-600">{error}</p>}
        <div className="flex items-center gap-3 pt-1">
          <button type="submit" disabled={saving || !name.trim() || !email.trim()}
            className="px-4 py-2 bg-reset-terracotta text-white text-sm font-medium rounded-xl hover:bg-reset-brown transition-colors disabled:opacity-50">
            {saving ? 'Creating…' : isTrial ? 'Create trial & send invite' : 'Create member'}
          </button>
          <button type="button" onClick={onClose} className="text-sm text-gray-500 hover:text-reset-brown">Cancel</button>
        </div>
      </form>
    </div>
  );
}

// ─── Stat card ────────────────────────────────────────────────────────────────
function StatCard({ label, value, unit = '' }) {
  return (
    <div className="bg-white rounded-xl shadow-sm px-4 py-3 text-center">
      <p className="text-2xl font-bold text-reset-brown">
        {value ?? '—'}<span className="text-sm font-normal text-gray-400">{unit}</span>
      </p>
      <p className="text-xs text-gray-500 mt-0.5">{label}</p>
    </div>
  );
}
